import React, { useState, useEffect } from "react";
import { Routes, Route, useSearchParams, useLocation } from "react-router-dom";
import { ChakraProvider, Select, Flex, Box, Text, useToast } from "@chakra-ui/react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { REWARD_TIERS } from "./utils/rewards"; // Import rewards

// Firebase
import {
  messaging,
  onMessage,
  firestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "../src/firebase/firebaseConfig";

// Layouts
import DefaultLayout from "./layouts/DefaultLayout";
import DashboardLayout from "./layouts/DashboardLayout";

// Contexts & Auth
import AuthProvider from "./context/AuthProvider";
import QuotaProvider from "./context/QuotaContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Custom Theme
import CustomTheme from './theme';

// Notification Component
import Message from "../src/components/notification/Message";

// Routes Configuration
import useTranslatedRoutes from './routes';
// 🚨 **Maximum Verified Clicks Per Share (Prevent Abuse)**
const MAX_VERIFIED_CLICKS_PER_SHARE = 10;
const MIN_ENGAGEMENT_TIME = 15000; // 15 seconds minimum stay

const App = () => {
  const [currentTheme, setCurrentTheme] = useState(CustomTheme);
  const toast = useToast();

  const routes = useTranslatedRoutes();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("utm_user");
  const shareId = searchParams.get("share_id"); // Track shared link ID
  const location = useLocation();

  /**
   * ✅ Capture & Store Referral Code + UTM Parameters
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referredByCode = params.get("referredByCode");
    const utmUser = params.get("utm_user");

    if (referredByCode) {
      console.log(`📌 Captured Referral Code: ${referredByCode}`);
      sessionStorage.setItem("referredByCode", referredByCode); // Store temporarily
    }

    if (utmUser) {
      console.log(`🔍 Captured UTM User ID: ${utmUser}`);
      sessionStorage.setItem("utm_user", utmUser);
    }
  }, [location]);

  /**
   * 🎯 Listen for FCM Messages (Push Notifications)
   */
  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      toast(<Message notification={payload.notification} />);
    });
    return () => unsubscribe();
  }, []);

  /**
   * ✅ Verify & Award Clicks (Only When User Clicks a Shared Link)
   */
  useEffect(() => {
    if (!userId || !shareId) return;

    const trackClick = async () => {
    
      const shareRef = doc(firestore, "user_shares", shareId);
      const shareSnap = await getDoc(shareRef);
    
      if (shareSnap.exists()) {
        const shareData = shareSnap.data();
        let newClicks = (shareData.clicks || 0) + 1;
    
        // 🚀 Update the total click count
        await updateDoc(shareRef, { clicks: newClicks });
    
        console.log(`📌 Click Registered: ${newClicks} for Share ID: ${shareId}`);
      }
    };

    trackClick();
  }, [userId, shareId]);

  /**
   * ✅ Verify & Award Clicks (With Fraud Prevention)
   */
  useEffect(() => {
    if (!userId || !shareId) return;

    const trackVerifiedClick = async () => {
      const shareRef = doc(firestore, "user_shares", shareId);
      const shareSnap = await getDoc(shareRef);

      if (shareSnap.exists()) {
        const shareData = shareSnap.data();
        let newVerifiedClicks = (shareData.verifiedClicks || 0) + 1;

        // 🚨 **Prevent Self-Clicking & Repeated Clicks**
        if (localStorage.getItem(`clicked_${shareId}`)) {
          console.warn("🚨 Click already counted for this user.");
          return;
        }

        // 🚨 **Ensure Minimum Engagement Time**
        const startTime = Date.now();
        setTimeout(async () => {
          const timeSpent = Date.now() - startTime;
          if (timeSpent < MIN_ENGAGEMENT_TIME) {
            console.warn("🚨 Engagement too short. Click not verified.");
            return;
          }

          // ✅ **Prevent Fraud: Limit to MAX_VERIFIED_CLICKS_PER_SHARE**
          if (newVerifiedClicks <= MAX_VERIFIED_CLICKS_PER_SHARE) {
            await updateDoc(shareRef, { verifiedClicks: newVerifiedClicks });

            console.log(`✅ Verified Click Recorded for ${userId}: ${newVerifiedClicks}`);

            // ✅ **Mark as counted to prevent double counting**
            localStorage.setItem(`clicked_${shareId}`, "true");

            // 🎯 Check if the user qualifies for a reward
            await checkAndAwardRewards(userId);
          } else {
            console.warn("🚨 Max verified clicks reached for this share.");
          }
        }, MIN_ENGAGEMENT_TIME);
      }
    };

    trackVerifiedClick();
  }, [userId, shareId]);

  /**
   * 🎁 Function to check if a user qualifies for a milestone reward
   */
  const checkAndAwardRewards = async (userId) => {
    const userRef = doc(firestore, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return;
    let userData = userSnap.data();

    let totalVerifiedClicks = userData.totalVerifiedClicks || 0;
    let rewardsGiven = userData.rewardsGiven || {}; // Track awarded rewards
    let freeTrades = userData.freeTrades || 0;

    // 🔥 Update total verified clicks
    totalVerifiedClicks += 1;
    let updateData = { totalVerifiedClicks };

    let rewardMessage = "";

    // ✅ Award Extra Free Trade at 25 verified clicks
    if (totalVerifiedClicks >= 25 && !rewardsGiven["25"]) {
      updateData["rewardsGiven.25"] = true;
      updateData.freeTrades = freeTrades + 1; // Award free trade
      rewardMessage = "You've earned 1 extra free trade for reaching 25 verified shares! 🎉";

      toast({
        title: "🎉 Free Trade Earned!",
        description: rewardMessage,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await sendRewardNotification(userId, "Free Trade Earned!", rewardMessage);
    }

    // 🎯 Check for other milestone rewards
    for (const [milestone, message] of Object.entries(REWARD_TIERS)) {
      if (totalVerifiedClicks >= milestone && !rewardsGiven[milestone]) {
        updateData[`rewardsGiven.${milestone}`] = true;
        rewardMessage = message;

        toast({
          title: "🎖️ Milestone Achieved!",
          description: message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        await sendRewardNotification(userId, "Milestone Achieved!", message);
      }
    }

    // ✅ Update Firestore with new data if changes were made
    if (Object.keys(updateData).length > 0) {
      await updateDoc(userRef, updateData);
    }
  };

  // ✅ Function to Send Reward Notifications
  const sendRewardNotification = async (userId, title, message) => {
    const userNotificationsRef = collection(firestore, "users", userId, "notifications");
    await addDoc(userNotificationsRef, {
      title,
      message,
      type: "reward",
      alertStatus: "success",
      createdAt: new Date(),
      status: "unread",
    });

    console.log(`✅ Reward notification sent to user ${userId}: ${title}`);
  };

  return (
    <ChakraProvider theme={currentTheme}>
      <AuthProvider>
        <QuotaProvider>

          {/* Routes */}
          <Routes>
            {routes.map(({ path, component, protected: isProtected, layout }, index) => {
              if (isProtected) {
                return (
                  <Route
                    key={index}
                    path={path}
                    element={
                      <ProtectedRoute>
                        {layout === 'personal' ? (
                          <DashboardLayout>{component}</DashboardLayout>
                        ) : (
                          <DefaultLayout>{component}</DefaultLayout>
                        )}
                      </ProtectedRoute>
                    }
                  />
                );
              }

              return (
                <Route
                  key={index}
                  path={path}
                  element={component}
                />
              );
            })}
          </Routes>

          {/* Toast Notifications */}
          <ToastContainer position="top-right" autoClose={5000} />
        </QuotaProvider>
      </AuthProvider>
    </ChakraProvider>
  );
};

export default App;
